import {
  ChartConfiguration,
  ChartDataset,
  ChartOptions,
  LegendOptions,
  Scale,
  TooltipItem,
  TooltipOptions,
} from 'chart.js';
import dayjs from 'dayjs';
import { DeepPartial } from 'node_modules/chart.js/dist/types/utils';

import { TrialInsightsLegendOptions } from '../../models/trial-insights-legend.model';
import { TrialTimelineMilestoneCategoryDates } from '../../models/trial-insights.model';

export function chartData(milestoneCategoryDates: TrialTimelineMilestoneCategoryDates[]) {
  const timelineStart = dayjs(milestoneCategoryDates[0].startDate);

  return milestoneCategoryDates.reduce(
    (accumulator, milestoneDates) => {
      // Buffer days (for stacked chart)
      const milestoneStart = dayjs(milestoneDates.startDate);
      const buffer = milestoneStart.diff(timelineStart, 'days');

      // Total days
      const total = milestoneDates.totalDays;

      accumulator.push([buffer, total + buffer]);

      return accumulator;
    },
    [] as [number, number][]
  );
}

export function chartOptions(
  totalDays: number,
  quarters: string[],
  timelineStart: string
): ChartConfiguration<'bar'>['options'] {
  return {
    elements: elementOptions(),
    indexAxis: 'y',
    maintainAspectRatio: false,
    plugins: pluginOptions(),
    responsive: true,
    scales: {
      ...xAxesOptions(totalDays, quarters, timelineStart),
      ...yAxesOptions(),
    },
  };
}

export function createDatasets(
  milestoneCategoryDates: TrialTimelineMilestoneCategoryDates[]
): ChartDataset<'bar'>[] {
  return [
    {
      backgroundColor: ['#226262', '#437f7f', '#4e6d79'],
      barThickness: 35,
      data: chartData(milestoneCategoryDates),
      hoverBackgroundColor: ['#3da0a0', '#489898', '#548092'],
      xAxisID: 'x1',
    },
  ];
}

export function createLegend(labels?: string[]): TrialInsightsLegendOptions {
  const legendOptions = {
    centered: true,
    color: '#f3f6f7',
    compact: true,
    data: [],
    divider: true,
  } as TrialInsightsLegendOptions;

  if (!labels?.length) {
    return legendOptions;
  }

  const colors = ['#226262', '#437f7f', '#4e6d79'];

  labels.forEach((label, index) => {
    legendOptions.data.push({
      displayDivider: false,
      displayIcon: true,
      iconColor: colors[index],
      title: label,
      value: '',
      valueColor: colors[index],
    });
  });

  return legendOptions;
}

export function defaultChart(): ChartConfiguration<'bar'> {
  return {
    data: {
      datasets: [],
    },
    options: {},
    type: 'bar',
  };
}

export function elementOptions(): ChartOptions<'bar'>['elements'] {
  return {
    bar: {
      borderColor: '#bacad0',
    },
  };
}

export function legendOptions(): DeepPartial<LegendOptions<'bar'>> {
  return {
    display: false,
  };
}

export function pluginOptions(): ChartOptions<'bar'>['plugins'] {
  return {
    legend: legendOptions(),
    tooltip: {
      ...tooltipOptions(),
    },
  };
}

export function tooltipOptions(): DeepPartial<TooltipOptions<'bar'>> {
  return {
    callbacks: {
      label(tooltipItem: TooltipItem<'bar'>) {
        const raw = tooltipItem.raw as [number, number];
        const totalDays = raw[1] - raw[0];
        const totalMonths = dayjs().add(totalDays, 'days').diff(dayjs(), 'months');

        return `Months: ${totalMonths}`;
      },
    },
  };
}

export function xAxesOptions(
  totalDays: number,
  quarters: string[],
  timelineStart: string
): ChartOptions<'bar'>['scales'] {
  return {
    x1: {
      beginAtZero: true,
      grid: {
        color: '#bacad0',
        lineWidth: 0.75,
        z: 2,
      },
      max: totalDays,
      stacked: true,
      ticks: {
        autoSkip: false,
        callback: (_, index) => {
          return quarters[index];
        },
        stepSize: 91,
      },
      title: {
        display: false,
      },
    },
    // this is only for the black line in the chart
    x2: {
      afterBuildTicks(axis: Scale) {
        const startDate = dayjs(timelineStart);
        const fromToday = dayjs().diff(startDate, 'days');

        axis.ticks = fromToday > totalDays ? [] : [{ value: fromToday }];
      },
      beginAtZero: true,
      grid: {
        color: 'black',
        drawTicks: false,
        lineWidth: 1.5,
        tickLength: 10,
        z: 1,
      },
      max: totalDays,
      ticks: {
        callback: () => '',
        display: false,
        stepSize: 91,
      },
    },
  };
}

export function yAxesOptions(): ChartOptions<'bar'>['scales'] {
  return {
    y: {
      display: false,
    },
  };
}
